import { resolveCategoryCode } from '@/lib/classColors'
import { type ClassUsageState } from '@/components/shows/class-usage-donut'
import { ClubAwardsCard } from '@/components/club-awards'
import { HeroAwardCard } from '@/components/hero-award'
import { lazy, Suspense, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { UseQueryResult } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { useRunMode } from '@/context/run-mode'
import { useCurrentShow } from '@/context/current-show'
import {
  useDivisions,
  useExhibitors,
  useReportAwards,
  useClubParticipationReport,
  useReportResults,
  useUpdateShow,
} from '@/api/hooks'
import { useEntryIntakeManager, type PendingIntakeEntry } from '@/hooks/useEntryIntake'
import { useEnteredClasses } from '@/hooks/useEnteredClasses'
import { CurrentShowSummary, SelectShowNotice } from '@/components/current-show'
import { toast } from 'sonner'
import { useActorPermissions, formatRoleList } from '@/hooks/useActorPermissions'
import { buildStatusOptions, STATUS_LABELS, STATUS_TRANSITIONS } from '@/lib/show-status'
import { summarizeJudgingProgress } from '@/lib/run-section-status'
import type {
  ReportAwardsOutput,
  ReportAwardWinnerOutput,
  ReportResultsOutput,
  ShowStatsOutput,
  ShowStatsWinnerOutput,
  TExhibitorWithEntries,
  TShow,
} from '@schemas'
import { PageHeading } from '@/components/page-heading'
import { showAware } from '@/lib/paths'
import { useShowSlug } from '@/hooks/useShowSlug'
import { Input } from '@/components/ui/input'
import { useLabelPrinter, useLabelPrinterDiscovery } from '@/context/label-printer'
import { resolveLabelsPerExhibitFromClass } from '@/lib/labels-per-exhibit'
import { showAllowsLabelPrinting } from '@/lib/label/permissions'
import { LabelPrintingDisabledNotice } from '@/components/label-printing-disabled-notice'
import { LabelPrinterPopoverControl } from '@/components/label-printer-popover'
import { Skeleton } from '@/components/ui/skeleton'

const ClassUsageDonut = lazy(() =>
  import('@/components/shows/class-usage-donut').then(module => ({
    default: module.ClassUsageDonut,
  }))
)
const EntriesPerEntrantChart = lazy(() =>
  import('@/components/shows/entries-per-entrant-chart').then(module => ({
    default: module.EntriesPerEntrantChart,
  }))
)
import { cn } from '@/lib/utils'
import {
  buildDivisionWinners,
  buildHeroAwardWinner,
  type DivisionWinnerSummary,
} from '@/lib/report-results'
import {
  ShowStatsSummary,
  type ShowStatsSummaryQuery,
  useShowStatsSummary,
} from '@/components/show-stats-summary'
import { ShowEditionComparison } from '@/components/shows/show-edition-comparison'
import { apiClient } from '@/lib/apiClient'
import { parseApiErrorStatus } from '@/lib/api-errors'
import { ReleaseStatusNote } from '@/features/show-release/ReleaseControls'

function summarizePending(entries: PendingIntakeEntry[]): number {
  return entries.filter(entry => entry.status !== 'syncing').length
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUuid(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value)
}

type ClassIdentifierSource = {
  id?: string | null
  code?: string | null
}

function getClassIdentifier(source: ClassIdentifierSource | null | undefined): string | null {
  if (!source) return null
  const id = typeof source.id === 'string' ? source.id.trim() : ''
  if (id) return id
  const code = typeof source.code === 'string' ? source.code.trim() : ''
  return code || null
}

function hasFinalizableEntries(exhibitor: TExhibitorWithEntries | undefined): boolean {
  if (!exhibitor) return false
  if (!Array.isArray(exhibitor.entries)) return false
  const entries = exhibitor.entries.filter(Boolean)
  return entries.length > 0
}

type FinalizationSnapshot = {
  finalizableEntrants: Array<TExhibitorWithEntries & { id: string }>
  totals: {
    unfinalized: number
    blockedMissingEntries: number
    blockedInvalidId: number
  }
}

export default function RunDashboard() {
  const { mode } = useRunMode()
  const { currentShow, setCurrentShow } = useCurrentShow()
  const divisionsQuery = useDivisions(currentShow?.id)
  const permissions = useActorPermissions(currentShow?.id)
  const exhibitorsQuery = useExhibitors(
    { showId: currentShow?.id, limit: 500 },
    { enabled: Boolean(currentShow?.id) && permissions.can('exhibitors:write') }
  )
  // Stewards may open this dashboard without stats access; skip the read the server would refuse.
  const canReadStats = permissions.can('reports:results')
  const showStatsQuery = useShowStatsSummary(canReadStats ? currentShow?.id : undefined)
  const enteredClasses = useEnteredClasses(currentShow?.id)
  const finalizedShowId =
    currentShow?.id && currentShow.status === 'finalized' ? currentShow.id : undefined
  const reportAwardsQuery = useReportAwards(finalizedShowId)
  // Club leaders are live in every phase, from the club report rather than the full awards report.
  const showClubAwards =
    canReadStats && currentShow?.enableClubs !== false && !currentShow?.labelOnlyMode
  const clubReportQuery = useClubParticipationReport(showClubAwards ? currentShow?.id : undefined, {
    includeUnknown: false,
  })
  const reportResultsQuery = useReportResults(finalizedShowId)
  const updateShow = useUpdateShow()
  const intakeState = useEntryIntakeManager(currentShow?.id)
  const [nextStatus, setNextStatus] = useState<TShow['status']>(currentShow?.status ?? 'draft')
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false)
  const [bulkFinalizingEntrants, setBulkFinalizingEntrants] = useState(false)
  const [finalizationSnapshot, setFinalizationSnapshot] = useState<FinalizationSnapshot | null>(
    null
  )
  const [finalizationLoading, setFinalizationLoading] = useState(false)
  const [finalizationError, setFinalizationError] = useState<string | null>(null)
  const finalizeDialogOutcome = useRef<'complete' | null>(null)
  const currentSlug = useShowSlug()

  const codePool = useMemo(() => {
    const available = intakeState.availableCodes.length
    const pending = summarizePending(intakeState.pendingEntries)
    return { available, pending }
  }, [intakeState])

  useEffect(() => {
    if (!currentShow) return
    setNextStatus(currentShow.status)
  }, [currentShow?.status])

  const staffAssignments = Array.isArray(permissions.staffQuery?.data)
    ? (permissions.staffQuery?.data ?? [])
    : []
  const divisions = Array.isArray(divisionsQuery.data) ? divisionsQuery.data : []
  const exhibitors = Array.isArray(exhibitorsQuery.data) ? exhibitorsQuery.data : []
  const unfinalizedEntrants = useMemo(
    () => exhibitors.filter(exhibitor => !exhibitor.finalizedAt),
    [exhibitors]
  )
  const previewEntrantsWithEntries = useMemo(
    () => unfinalizedEntrants.filter(exhibitor => hasFinalizableEntries(exhibitor)),
    [unfinalizedEntrants]
  )
  const previewFinalizableEntrants = useMemo(
    () =>
      previewEntrantsWithEntries.filter(
        (exhibitor): exhibitor is TExhibitorWithEntries & { id: string } =>
          isUuid(exhibitor.id) && hasFinalizableEntries(exhibitor)
      ),
    [previewEntrantsWithEntries]
  )
  const previewBlockedMissingEntries = useMemo(() => {
    return unfinalizedEntrants.reduce((count, exhibitor) => {
      if (!hasFinalizableEntries(exhibitor)) {
        return count + 1
      }
      return count
    }, 0)
  }, [unfinalizedEntrants])
  const previewBlockedInvalidId = useMemo(() => {
    return previewEntrantsWithEntries.reduce((count, exhibitor) => {
      if (!isUuid(exhibitor.id)) {
        return count + 1
      }
      return count
    }, 0)
  }, [previewEntrantsWithEntries])
  const divisionsReady = Array.isArray(divisionsQuery.data)
  const staffReady = Array.isArray(permissions.staffQuery?.data)
  const showAllowsIntake = currentShow?.status === 'accepting_entries'

  const hasClasses =
    divisionsReady &&
    divisions.some(division => Array.isArray(division.classes) && division.classes.length > 0)
  const hasJudges = staffReady && staffAssignments.some(assignment => assignment.role === 'judge')
  const hasLeadSecretaries =
    staffReady && staffAssignments.some(assignment => assignment.role === 'lead_secretary')
  const hasSecretaries =
    hasLeadSecretaries ||
    (staffReady && staffAssignments.some(assignment => assignment.role === 'secretary'))

  const enteredClassIdList = enteredClasses.entered?.classIds
  const classUsageSnapshot = useMemo(() => {
    const allClassIds = new Set<string>()
    const classCategories = new Map<string, string>()

    divisions.forEach(division => {
      division.classes?.forEach(showClass => {
        const identifier = getClassIdentifier(showClass)
        if (identifier) {
          allClassIds.add(identifier)
          classCategories.set(
            identifier,
            resolveCategoryCode(showClass.catalogClass?.categoryCode, division.code ?? '')
          )
        }
      })
    })

    const enteredClassIdsInShow = Array.from(new Set(enteredClassIdList)).filter(id =>
      allClassIds.has(id)
    )
    const classesWithEntriesCount = enteredClassIdsInShow.length
    const totalClasses = allClassIds.size
    const unusedClasses = Math.max(totalClasses - classesWithEntriesCount, 0)

    const categoryCounts = new Map<string, number>()
    enteredClassIdsInShow.forEach(id => {
      const code = classCategories.get(id) ?? ''
      categoryCounts.set(code, (categoryCounts.get(code) ?? 0) + 1)
    })

    return {
      categories: Array.from(categoryCounts, ([code, count]) => ({ code, count })),
      totalClasses,
      classesWithEntries: classesWithEntriesCount,
      unusedClasses,
      enteredClassIds: enteredClassIdsInShow,
    }
  }, [divisions, enteredClassIdList])

  const statusOptions = useMemo(() => {
    if (!currentShow) return []

    if (!divisionsReady || !staffReady) {
      const transitions = STATUS_TRANSITIONS[currentShow.status] ?? [currentShow.status]
      return transitions.map(value => ({
        value,
        label: STATUS_LABELS[value],
        disabled: value !== currentShow.status,
        reason: value !== currentShow.status ? 'Loading show configuration…' : undefined,
      }))
    }

    return buildStatusOptions({
      show: currentShow,
      hasClasses,
      hasJudges,
      hasSecretaries,
      hasLeadSecretaries,
    })
  }, [
    currentShow,
    divisionsReady,
    staffReady,
    hasClasses,
    hasJudges,
    hasSecretaries,
    hasLeadSecretaries,
  ])

  const canManageStatus = permissions.can('show:write')
  const permissionLoading = permissions.staffQuery?.isLoading ?? false
  const statusRolesLabel = formatRoleList(permissions.getAllowedRoles('show:write'))
  const classUsage: ClassUsageState = {
    showId: currentShow?.id ?? undefined,
    snapshot: classUsageSnapshot,
    isLoading: divisionsQuery.isLoading || enteredClasses.isLoading,
    isError: enteredClasses.isError,
    error: enteredClasses.error,
  }
  const currentStatusLabel = currentShow ? STATUS_LABELS[currentShow.status] : null
  const nextStatusLabel = STATUS_LABELS[nextStatus]
  const pendingFinalizationCount =
    finalizationSnapshot?.totals.unfinalized ?? unfinalizedEntrants.length
  const eligibleFinalizationCount =
    finalizationSnapshot?.finalizableEntrants.length ?? previewFinalizableEntrants.length
  const blockedMissingEntriesCount =
    finalizationSnapshot?.totals.blockedMissingEntries ?? previewBlockedMissingEntries
  const blockedInvalidIdCount =
    finalizationSnapshot?.totals.blockedInvalidId ?? previewBlockedInvalidId
  const canBulkFinalize =
    Boolean(finalizationSnapshot) &&
    eligibleFinalizationCount > 0 &&
    !finalizationLoading &&
    !finalizationError
  const eligibleCountLabel = eligibleFinalizationCount === 1 ? 'entrant' : 'entrants'
  const missingEntriesCountLabel = blockedMissingEntriesCount === 1 ? 'entrant' : 'entrants'
  const invalidIdCountLabel = blockedInvalidIdCount === 1 ? 'entrant' : 'entrants'
  const nextStatusDisplay = nextStatusLabel ?? STATUS_LABELS['judging'] ?? 'Judging'
  const finalizeButtonLabel = bulkFinalizingEntrants
    ? 'Finalizing…'
    : finalizationLoading
      ? 'Loading entrants…'
      : canBulkFinalize
        ? `Finalize ${eligibleFinalizationCount} ${eligibleCountLabel} and switch to ${nextStatusDisplay}`
        : 'Finalize entrants and switch to Judging'

  const commitShowStatusUpdate = (status: TShow['status']) => {
    if (!currentShow?.id) return
    if (status === currentShow.status) return

    const currentStatus = currentShow.status
    const showId = currentShow.id
    updateShow.mutate(
      {
        showId,
        payload: {
          status,
          ...(currentStatus === 'accepting_entries' && status === 'judging'
            ? { finalizeEntrantsForJudging: true as const }
            : {}),
        },
      },
      {
        onSuccess: updated => {
          setCurrentShow(updated)
          if (updated.status === 'finalized') {
            void showStatsQuery.refetch()
          }
          toast.success(`Show moved to ${STATUS_LABELS[updated.status]}`)
        },
        onError: error => {
          toast.error(error.message)
          setNextStatus(currentStatus)
        },
      }
    )
  }

  const loadFinalizationSnapshot = useCallback(async () => {
    if (!currentShow?.id) return null
    setFinalizationLoading(true)
    setFinalizationError(null)
    setFinalizationSnapshot(null)
    try {
      const limit = 500
      let offset = 0
      let hasMore = true
      const finalizable: Array<TExhibitorWithEntries & { id: string }> = []
      let unfinalized = 0
      let blockedMissingEntries = 0
      let blockedInvalidId = 0
      while (hasMore) {
        const { exhibitors } = await apiClient.listExhibitors({
          showId: currentShow.id,
          limit,
          offset,
        })
        const batch = exhibitors ?? []
        if (batch.length === 0) {
          hasMore = false
          continue
        }
        for (const exhibitor of batch) {
          if (exhibitor.finalizedAt) continue
          unfinalized += 1
          if (!hasFinalizableEntries(exhibitor)) {
            blockedMissingEntries += 1
            continue
          }
          if (!isUuid(exhibitor.id)) {
            blockedInvalidId += 1
            continue
          }
          finalizable.push({ ...exhibitor, id: exhibitor.id })
        }
        if (batch.length < limit) {
          hasMore = false
        } else {
          offset += batch.length
        }
      }

      const snapshot: FinalizationSnapshot = {
        finalizableEntrants: finalizable,
        totals: {
          unfinalized,
          blockedMissingEntries,
          blockedInvalidId,
        },
      }
      setFinalizationSnapshot(snapshot)
      return snapshot
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to load entrants for finalization.'
      setFinalizationError(message)
      setFinalizationSnapshot(null)
      return null
    } finally {
      setFinalizationLoading(false)
    }
  }, [currentShow?.id])

  const handleUpdateStatus = async () => {
    if (!currentShow) return
    if (nextStatus === currentShow.status) return

    const movingToJudging = nextStatus === 'judging' && currentShow.status !== 'judging'

    if (movingToJudging) {
      setFinalizeDialogOpen(true)
      const snapshot = await loadFinalizationSnapshot()
      if (snapshot && snapshot.totals.unfinalized === 0) {
        finalizeDialogOutcome.current = 'complete'
        setFinalizeDialogOpen(false)
        commitShowStatusUpdate(nextStatus)
      }
      return
    }

    commitShowStatusUpdate(nextStatus)
  }

  const handleFinalizeDialogChange = (open: boolean) => {
    if (open) {
      setFinalizeDialogOpen(true)
      finalizeDialogOutcome.current = null
      return
    }

    if (bulkFinalizingEntrants) {
      setFinalizeDialogOpen(true)
      return
    }

    setFinalizeDialogOpen(false)
    setFinalizationSnapshot(null)
    setFinalizationError(null)

    if (finalizeDialogOutcome.current === 'complete') {
      finalizeDialogOutcome.current = null
      return
    }

    if (currentShow) {
      setNextStatus(currentShow.status)
    }
  }

  const handleFinalizeCancel = () => {
    if (bulkFinalizingEntrants) return
    handleFinalizeDialogChange(false)
  }

  const handleFinalizeEntrantsAndMoveToJudging = async () => {
    if (!currentShow?.id) return

    let snapshot = finalizationSnapshot
    if (!snapshot && !finalizationLoading) {
      snapshot = await loadFinalizationSnapshot()
    }

    if (!snapshot) {
      const message =
        finalizationError ?? 'Unable to load entrants eligible for finalization. Try again later.'
      toast.error(message)
      return
    }

    const { finalizableEntrants: entrantsToFinalize, totals } = snapshot

    if (entrantsToFinalize.length === 0) {
      const needsEntries = totals.blockedMissingEntries > 0
      const needsManual = totals.blockedInvalidId > 0
      let detail = 'Review Check-In to continue.'
      if (needsEntries && needsManual) {
        detail = 'Add entries or finalize the remaining entrants individually from Check-In.'
      } else if (needsEntries) {
        detail = 'Add entries for pending entrants in Check-In.'
      } else if (needsManual) {
        detail = 'Finalize the remaining entrants individually from Check-In.'
      }
      toast.error(`No entrants are ready to finalize automatically. ${detail}`)
      return
    }

    const countLabel = entrantsToFinalize.length === 1 ? 'entrant' : 'entrants'

    setBulkFinalizingEntrants(true)
    let completed = false

    try {
      toast.info(`Finalizing ${entrantsToFinalize.length} ${countLabel} before judging…`)

      const updated = await updateShow.mutateAsync({
        showId: currentShow.id,
        payload: { status: 'judging', finalizeEntrantsForJudging: true },
      })
      setCurrentShow(updated)

      if (typeof exhibitorsQuery.refetch === 'function') {
        void exhibitorsQuery.refetch()
      }

      const followUps: string[] = []
      if (totals.blockedMissingEntries > 0) {
        followUps.push(
          `${totals.blockedMissingEntries} ${totals.blockedMissingEntries === 1 ? 'entrant' : 'entrants'} still ${
            totals.blockedMissingEntries === 1 ? 'needs' : 'need'
          } entries.`
        )
      }
      if (totals.blockedInvalidId > 0) {
        followUps.push(
          `${totals.blockedInvalidId} ${totals.blockedInvalidId === 1 ? 'entrant' : 'entrants'} ${
            totals.blockedInvalidId === 1 ? 'must' : 'must'
          } be finalized individually from Check-In.`
        )
      }
      const suffix = followUps.length > 0 ? ` ${followUps.join(' ')}` : ''
      toast.success(`Finalized ${entrantsToFinalize.length} ${countLabel} for judging.${suffix}`)
      completed = true
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to finalize entrants at this time.'
      toast.error(message)
      await loadFinalizationSnapshot()
    } finally {
      setBulkFinalizingEntrants(false)
    }

    if (completed) {
      finalizeDialogOutcome.current = 'complete'
      setFinalizeDialogOpen(false)
    }
  }

  const judgingProgress = useMemo(() => {
    const includedClassIds = new Set(classUsageSnapshot.enteredClassIds)
    return summarizeJudgingProgress(divisionsQuery.data, { includedClassIds })
  }, [divisionsQuery.data, classUsageSnapshot.enteredClassIds])

  if (!currentShow) {
    return (
      <SelectShowNotice
        title="Pick a show to enter Run mode"
        description="Run dashboards require a selected show. Choose one from the shows list."
      />
    )
  }

  if (currentShow.labelOnlyMode) {
    return (
      <LabelOnlyDashboard
        show={currentShow}
        divisions={divisions}
        isLoadingDivisions={divisionsQuery.isLoading}
      />
    )
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <PageHeading>Show Dashboard</PageHeading>
        <p className="text-muted-foreground">
          See what’s entered in the show and how far judging has progressed.
        </p>
      </header>

      {mode !== 'run' && <CurrentShowSummary show={currentShow} />}

      <div className={cn('grid gap-4', currentShow.status === 'judging' && 'md:grid-cols-2')}>
        <Card>
          <CardHeader>
            <CardTitle>Show Status</CardTitle>
            <CardDescription>
              Advance the show through intake, judging, and reporting.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Current</span>
              <Badge variant="secondary">{STATUS_LABELS[currentShow.status]}</Badge>
            </div>
            {permissionLoading ? (
              <p className="text-sm text-muted-foreground">Loading permissions…</p>
            ) : canManageStatus ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="run-status-select" className="text-sm font-medium leading-none">
                    Change status
                  </Label>
                  <select
                    id="run-status-select"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={nextStatus}
                    onChange={event => setNextStatus(event.target.value as TShow['status'])}
                    disabled={updateShow.isPending}
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {statusOptions.some(option => option.disabled && option.reason) && (
                  <div className="space-y-1 text-xs text-muted-foreground">
                    {statusOptions
                      .filter(option => option.disabled && option.reason)
                      .map(option => (
                        <p key={option.value}>
                          {option.label}: {option.reason}
                        </p>
                      ))}
                  </div>
                )}
                <Button
                  type="button"
                  onClick={() => void handleUpdateStatus()}
                  disabled={nextStatus === currentShow.status || updateShow.isPending}
                >
                  {updateShow.isPending ? 'Updating…' : 'Update status'}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Managing status requires {statusRolesLabel}.
              </p>
            )}
          </CardContent>
        </Card>

        {currentShow.status === 'judging' && (
          <Card>
            <CardHeader>
              <CardTitle>Judging Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <JudgingProgressMetric
                label="Classes started"
                displayValue={`${judgingProgress.started} / ${judgingProgress.total}`}
                value={judgingProgress.started}
                max={judgingProgress.total}
                colorMode="positive"
              />
              <JudgingProgressMetric
                label="Finalized"
                displayValue={`${judgingProgress.finalized} / ${judgingProgress.total}`}
                value={judgingProgress.finalized}
                max={judgingProgress.total}
                colorMode="positive"
                highlightClass="text-primary"
              />
              <JudgingProgressMetric
                label="No awards"
                displayValue={String(judgingProgress.noAwards)}
                value={judgingProgress.noAwards}
                max={judgingProgress.total}
                colorMode="inverse"
              />
              <p className="text-xs text-muted-foreground">
                Empty classes: {classUsageSnapshot.unusedClasses}
              </p>
              <Button asChild size="sm" variant="outline" className="self-start">
                <Link to={showAware('/judging', { currentSlug })}>Open Judging</Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {currentShow.status === 'judging' && <HeroAwardCard show={currentShow} />}

      {permissions.can('reports:results') && (
        <div className="flex justify-end">
          <Button asChild size="sm" variant="outline">
            <Link to={showAware('/run/board', { currentSlug })}>Open show day board</Link>
          </Button>
        </div>
      )}

      {canReadStats && (
        <ShowStatsCard
          query={showStatsQuery}
          showStatus={currentShow.status}
          enableClubs={currentShow.enableClubs !== false}
          awardsQuery={reportAwardsQuery}
          resultsQuery={reportResultsQuery}
          classUsage={classUsage}
        />
      )}
      {showClubAwards && <ClubAwardsCard query={clubReportQuery} />}
      {canReadStats && <ShowEditionComparison key={currentShow.id} show={currentShow} />}
      {currentShow.status === 'finalized' && currentShow.id && (
        <ReleaseStatusNote showId={currentShow.id} />
      )}
      {currentShow.seriesMembership?.series && (
        <Button asChild variant="outline">
          <Link to={showAware('/series-report', { currentSlug })}>Open series trends</Link>
        </Button>
      )}

      {showAllowsIntake && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Code Pool</CardTitle>
              <CardDescription>Track the available entry codes for intake.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <div className="flex justify-between text-sm">
                <span>Available codes</span>
                <span className="font-semibold text-primary">{codePool.available}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Pending intake</span>
                <span className="font-semibold text-muted-foreground">{codePool.pending}</span>
              </div>
              <Button asChild size="sm" className="mt-2 self-start">
                <Link to={showAware('/check-in', { currentSlug })}>Fetch 10 codes</Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pending Offline Work</CardTitle>
              <CardDescription>Entries queued locally awaiting sync.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <p className="text-2xl font-semibold">{codePool.pending}</p>
              <p className="text-sm text-muted-foreground">
                Review and sync pending entries from the intake workspace to keep data current.
              </p>
              <Button asChild size="sm" variant="outline" className="self-start">
                <Link to={showAware('/check-in', { currentSlug })}>Go to Check-In</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
      <Dialog open={finalizeDialogOpen} onOpenChange={handleFinalizeDialogChange}>
        <DialogContent contentClassName="sm:max-w-lg">
          <DialogHeader className="space-y-2">
            <DialogTitle>Finalize entrants before judging</DialogTitle>
            <DialogDescription>
              Finalize the remaining entrants so the show can move to the Judging phase.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            {bulkFinalizingEntrants && (
              <p role="status" aria-live="polite" className="text-muted-foreground">
                Finalizing entrants and preparing judging. Please keep this window open.
              </p>
            )}
            <p>
              There {pendingFinalizationCount === 1 ? 'is' : 'are'}{' '}
              <span className="font-medium">{pendingFinalizationCount}</span>{' '}
              {pendingFinalizationCount === 1 ? 'entrant' : 'entrants'} without a finalized
              check-in.
            </p>
            {finalizationLoading && (
              <p className="rounded-md border border-muted/30 bg-muted/20 p-3 text-xs text-muted-foreground">
                Loading entrant status…
              </p>
            )}
            {finalizationError && (
              <div
                role="alert"
                className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive"
              >
                <p>{finalizationError}</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void loadFinalizationSnapshot()}
                >
                  Retry
                </Button>
              </div>
            )}
            {!finalizationLoading && !finalizationError && (
              <>
                {canBulkFinalize ? (
                  <p className="text-muted-foreground">
                    {eligibleFinalizationCount === pendingFinalizationCount
                      ? 'All remaining entrants will be finalized automatically.'
                      : `${eligibleFinalizationCount} ${eligibleCountLabel} will be finalized automatically.`}
                  </p>
                ) : (
                  <p className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                    No entrants are ready to finalize automatically. Add entries or finalize the
                    remaining entrants individually from Check-In before moving to Judging.
                  </p>
                )}
                {blockedMissingEntriesCount > 0 && (
                  <p className="rounded-md border border-muted/30 bg-muted/30 p-3 text-xs text-muted-foreground">
                    {blockedMissingEntriesCount} {missingEntriesCountLabel}{' '}
                    {blockedMissingEntriesCount === 1 ? 'still needs' : 'still need'} entries.
                    Finalize them or mark them Not Entered from Check-In once entries are resolved.
                  </p>
                )}
                {blockedInvalidIdCount > 0 && (
                  <p className="rounded-md border border-muted/30 bg-muted/30 p-3 text-xs text-muted-foreground">
                    {blockedInvalidIdCount} {invalidIdCountLabel}{' '}
                    {blockedInvalidIdCount === 1 ? 'must' : 'must'} be finalized individually from
                    Check-In.
                  </p>
                )}
              </>
            )}
            <p className="text-muted-foreground">Finalizing now will:</p>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Lock intake for this show so no new entries can be added.</li>
              <li>Enable judging workflows, including marking entries Not Entered.</li>
            </ul>
            <p className="text-xs text-muted-foreground">
              You can reopen individual entrants from Check-In if adjustments are needed later.
            </p>
          </div>
          <DialogFooter className="sm:flex-col-reverse">
            <Button
              type="button"
              variant="outline"
              onClick={handleFinalizeCancel}
              className="h-auto min-h-9 w-full whitespace-normal"
              disabled={bulkFinalizingEntrants || finalizationLoading || updateShow.isPending}
            >
              {currentStatusLabel ? `Keep ${currentStatusLabel}` : 'Keep current phase'}
            </Button>
            <Button
              type="button"
              onClick={() => void handleFinalizeEntrantsAndMoveToJudging()}
              className="h-auto min-h-9 w-full whitespace-normal"
              disabled={
                bulkFinalizingEntrants ||
                finalizationLoading ||
                updateShow.isPending ||
                !canBulkFinalize
              }
            >
              {finalizeButtonLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

type ShowStatsCardProps = {
  query: ShowStatsSummaryQuery
  showStatus: TShow['status']
  enableClubs: boolean
  awardsQuery: UseQueryResult<ReportAwardsOutput, Error>
  resultsQuery: UseQueryResult<ReportResultsOutput, Error>
  classUsage: ClassUsageState
}

function ShowStatsCard({
  query,
  showStatus,
  enableClubs,
  awardsQuery,
  resultsQuery,
  classUsage,
}: ShowStatsCardProps) {
  const header = (
    <CardHeader>
      <CardTitle>Show Stats</CardTitle>
    </CardHeader>
  )

  const isLoading =
    query.isLoading ||
    (showStatus === 'finalized' && (awardsQuery.isLoading || resultsQuery.isLoading))
  const isRetrying =
    query.isFetching ||
    (showStatus === 'finalized' && (awardsQuery.isFetching || resultsQuery.isFetching))

  if (isLoading) {
    return (
      <Card aria-busy="true">
        {header}
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground" aria-live="polite">
            Loading show stats…
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Skeleton className="h-20 w-full rounded-md" />
            <Skeleton className="h-20 w-full rounded-md" />
            <Skeleton className="h-20 w-full rounded-md" />
          </div>
        </CardContent>
      </Card>
    )
  }

  const errors = [
    query.error,
    ...(showStatus === 'finalized' ? [awardsQuery.error, resultsQuery.error] : []),
  ].filter(Boolean)
  const error = errors.find(error => parseApiErrorStatus(error) !== 429) ?? errors[0]

  const isRateLimited = error && parseApiErrorStatus(error) === 429
  if (isRateLimited && !query.summary) {
    return (
      <Card>
        {header}
        <CardContent className="space-y-4">
          <p role="status" className="text-sm text-muted-foreground">
            Loading show stats. This will update automatically…
          </p>
        </CardContent>
      </Card>
    )
  }

  if (error && !isRateLimited) {
    return (
      <Card>
        {header}
        <CardContent className="space-y-4">
          <div
            role="alert"
            className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-4"
          >
            <p className="text-sm font-semibold text-destructive">Unable to load show stats.</p>
            {error instanceof Error && error.message && (
              <p className="text-sm text-destructive/80">{error.message}</p>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                void query.refetch()
                if (showStatus === 'finalized') {
                  void awardsQuery.refetch()
                  void resultsQuery.refetch()
                }
              }}
              disabled={isRetrying}
            >
              {isRetrying ? 'Retrying…' : 'Retry'}
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!query.summary) {
    return null
  }

  const winners = showStatus === 'finalized' ? query.summary.winners : null
  const awards = showStatus === 'finalized' ? awardsQuery.data : undefined
  const results = showStatus === 'finalized' ? resultsQuery.data : undefined
  const divisionWinners = showStatus === 'finalized' ? buildDivisionWinners(awards, results) : []
  const showDivisionAwards = awards?.bestInDivisionEnabled !== false

  return (
    <Card>
      {header}
      <CardContent className="space-y-6">
        <ShowStatsSummary
          summary={query.summary}
          enableClubs={enableClubs}
          variant="dashboard"
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
        />
        {isRateLimited && (
          <p role="status" className="text-sm text-muted-foreground">
            Showing the last available counts. Updating automatically…
          </p>
        )}
        <div className="grid gap-4 lg:grid-cols-2">
          <Suspense fallback={<Skeleton className="mx-auto h-40 w-40 rounded-full" />}>
            <ClassUsageDonut classUsage={classUsage} />
          </Suspense>
          <Suspense fallback={<Skeleton className="h-64 w-full rounded-md" />}>
            <EntriesPerEntrantChart
              distribution={query.data?.entriesPerEntrantDistribution ?? []}
            />
          </Suspense>
        </div>
        {showStatus === 'finalized' ? (
          winners ? (
            <WinnersSection
              winners={winners}
              divisionWinners={divisionWinners}
              showDivisionAwards={showDivisionAwards}
              showHeroAward={awards?.heroAward?.enabled === true}
              heroAwardWinner={buildHeroAwardWinner(awards, results)}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Winners will appear once awards are recorded for this show.
            </p>
          )
        ) : (
          <p className="text-sm text-muted-foreground">
            Finalize the show to reveal winners and special awards.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

type WinnersSectionProps = {
  winners: NonNullable<ShowStatsOutput['winners']>
  divisionWinners: DivisionWinnerSummary
  showDivisionAwards: boolean
  showHeroAward: boolean
  heroAwardWinner: ReportAwardWinnerOutput | null
}

function WinnersSection({
  winners,
  divisionWinners,
  showDivisionAwards,
  showHeroAward,
  heroAwardWinner,
}: WinnersSectionProps) {
  const bestInShow = winners.bestInShow ?? []
  return (
    <div className="space-y-4">
      <h4 className="text-lg font-semibold">Results</h4>
      <div className="grid gap-4 md:grid-cols-2">
        <WinnerTile title="Grand Champion" winner={winners.grandChampion ?? null} />
        <WinnerTile title="Black Jar Winner" winner={winners.blackJarWinner ?? null} />
        <BestInShowTiles winners={bestInShow} />
        {showHeroAward && <WinnerTile title="Hero Award" winner={heroAwardWinner} />}
      </div>
      {showDivisionAwards ? (
        <div className="space-y-2">
          <p className="text-sm font-semibold">Best in Division</p>
          <DivisionWinnersList divisions={divisionWinners} />
        </div>
      ) : null}
    </div>
  )
}

type DashboardWinner = ReportAwardWinnerOutput & { points?: number | null }

function WinnerTile({ title, winner }: { title: string; winner: DashboardWinner | null }) {
  const showPoints = title === 'Grand Champion'
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      {winner ? (
        <div className="mt-2">
          <WinnerDetails winner={winner} showPoints={showPoints} />
        </div>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">Not yet awarded.</p>
      )}
    </div>
  )
}

function BestInShowTiles({ winners }: { winners: ShowStatsWinnerOutput[] }) {
  if (winners.length === 0) {
    return (
      <div className="rounded-md border p-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Best in Show
        </p>
        <p className="mt-2 text-sm text-muted-foreground">No Best in Show winners recorded.</p>
      </div>
    )
  }

  return (
    <>
      {winners.map(winner => (
        <WinnerTile key={winner.entryId} title="Best in Show" winner={winner} />
      ))}
    </>
  )
}

function DivisionWinnersList({ divisions }: { divisions: DivisionWinnerSummary }) {
  if (divisions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No division champions have been identified.</p>
    )
  }

  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {divisions.map(division => (
        <li key={division.divisionId} className="rounded-md border border-dashed p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {division.divisionCode ? `${division.divisionCode} — ` : ''}
            {division.divisionName}
          </p>
          <div className="mt-1">
            <DivisionWinnerDetails winner={division.winner} />
          </div>
        </li>
      ))}
    </ul>
  )
}

function DivisionWinnerDetails({ winner }: { winner: ReportAwardWinnerOutput | null | undefined }) {
  if (!winner) {
    return <p className="text-sm text-muted-foreground">Not awarded.</p>
  }

  const exhibitorName = winner.exhibitorName?.trim() || 'Exhibitor pending'
  const classParts: string[] = []
  if (winner.classCode?.trim()) classParts.push(winner.classCode.trim())
  if (winner.className?.trim()) classParts.push(winner.className.trim())
  const identifierValue = winner.exhibitId ?? null
  const entrantLabel = winner.entrantName?.trim()
  const identifierLabel = identifierValue ? 'Exhibit ID' : null

  return (
    <div className="space-y-1 text-sm">
      <p className="font-semibold">{exhibitorName}</p>
      {identifierLabel && (
        <p className="text-muted-foreground">
          {identifierLabel}{' '}
          <span className="font-mono font-semibold text-foreground">
            {identifierValue ?? entrantLabel}
          </span>
        </p>
      )}
      {classParts.length > 0 ? (
        <p className="text-muted-foreground">Class: {classParts.join(' — ')}</p>
      ) : null}
    </div>
  )
}

function WinnerDetails({
  winner,
  showPoints = false,
}: {
  winner: DashboardWinner
  showPoints?: boolean
}) {
  const exhibitorName = winner.exhibitorName?.trim() || 'Exhibitor pending'
  const categoryParts: string[] = []
  if (winner.divisionName?.trim()) categoryParts.push(winner.divisionName.trim())
  if (winner.className?.trim()) categoryParts.push(winner.className.trim())
  const categoryLabel = categoryParts.join(' · ')
  const identifierValue = typeof winner.exhibitId === 'string' ? winner.exhibitId.trim() : ''
  const showIdentifier = identifierValue.length > 0
  const pointsValue = typeof winner.points === 'number' ? winner.points : null

  return (
    <div className="space-y-1 text-sm">
      <p className="font-semibold">{exhibitorName}</p>
      {showIdentifier ? (
        <p className="text-muted-foreground">
          Exhibit ID{' '}
          <span className="font-mono font-semibold text-foreground">{identifierValue}</span>
        </p>
      ) : null}
      {showPoints && pointsValue !== null ? (
        <p className="text-muted-foreground">
          Points <span className="font-semibold text-foreground">{pointsValue}</span>
        </p>
      ) : null}
      {categoryLabel && <p className="text-muted-foreground">Category: {categoryLabel}</p>}
    </div>
  )
}

type JudgingProgressMetricProps = {
  label: string
  displayValue: string
  value: number
  max: number
  colorMode: 'positive' | 'inverse'
  highlightClass?: string
}

function JudgingProgressMetric({
  label,
  displayValue,
  value,
  max,
  colorMode,
  highlightClass,
}: JudgingProgressMetricProps) {
  const clampedMax = Math.max(max, 1)
  const safeValue = Math.min(Math.max(value, 0), clampedMax)
  const percent = (safeValue / clampedMax) * 100
  const fillColor = resolveProgressColor(percent / 100, colorMode)
  const fillTestId = `progress-fill-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span>{label}</span>
        <span className={cn('font-semibold', highlightClass)}>{displayValue}</span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuenow={safeValue}
        aria-valuemax={clampedMax}
        aria-valuetext={`${safeValue} of ${clampedMax}`}
        className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          data-testid={fillTestId}
          className="h-full rounded-full transition-[width] duration-300 ease-out"
          style={{ width: `${percent}%`, backgroundColor: fillColor }}
        />
      </div>
    </div>
  )
}

const RED_RGB: [number, number, number] = [239, 68, 68] // Tailwind red-500
const EMERALD_RGB: [number, number, number] = [16, 185, 129] // Tailwind emerald-500

export function resolveProgressColor(ratio: number, mode: 'positive' | 'inverse'): string {
  const clamped = Math.min(Math.max(ratio, 0), 1)
  const [start, end] = mode === 'positive' ? [RED_RGB, EMERALD_RGB] : [EMERALD_RGB, RED_RGB]
  const r = Math.round(start[0] + (end[0] - start[0]) * clamped)
  const g = Math.round(start[1] + (end[1] - start[1]) * clamped)
  const b = Math.round(start[2] + (end[2] - start[2]) * clamped)
  return `rgb(${r}, ${g}, ${b})`
}

type LabelOnlyDashboardProps = {
  show: TShow
  divisions: Array<{
    id?: string | null
    name?: string | null
    code?: string | null
    classes?: Array<
      Record<string, unknown> & { id?: string | null; name?: string | null; code?: string | null }
    > | null
  }>
  isLoadingDivisions: boolean
}

type LabelOnlyClassOption = {
  id: string
  label: string
  code: string | null
  raw: Record<string, unknown>
}

function LabelOnlyDashboard({ show, divisions, isLoadingDivisions }: LabelOnlyDashboardProps) {
  const labelPrintingDisabled = show.labelPrintingDisabled ?? false
  const allowsLabelPrinting = showAllowsLabelPrinting(show)
  useLabelPrinterDiscovery(allowsLabelPrinting)
  const {
    printEntryLabel,
    strategy: labelStrategy,
    strategyLabel,
    printers,
    selectedPrinter,
    loadingPrinters,
    error,
    refreshPrinters,
    selectPrinter,
    setStrategy,
    testPrint,
    jobs,
  } = useLabelPrinter()
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  )
  const [selectedClassId, setSelectedClassId] = useState<string>('')
  const [exhibitId, setExhibitId] = useState('')
  const [copiesInput, setCopiesInput] = useState('1')
  const [isPrinting, setIsPrinting] = useState(false)
  const printControlsDisabled = isPrinting || labelPrintingDisabled

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }
  }, [])

  const classOptions = useMemo<LabelOnlyClassOption[]>(() => {
    const options: LabelOnlyClassOption[] = []
    divisions.forEach((division, divisionIndex) => {
      ;(division.classes ?? []).forEach((klass, classIndex) => {
        if (!klass) return
        const classId =
          typeof klass.id === 'string' && klass.id.length > 0
            ? klass.id
            : `${division.id ?? division.code ?? 'division'}:${divisionIndex}:${classIndex}`
        const classCode =
          typeof klass.code === 'string' && klass.code.trim().length > 0 ? klass.code.trim() : null
        const className =
          typeof klass.name === 'string' && klass.name.trim().length > 0 ? klass.name.trim() : null
        let label = className ?? 'Unnamed class'
        if (classCode) {
          label = className ? `${classCode} - ${className}` : classCode
        }
        options.push({
          id: classId,
          label: label || 'Unnamed class',
          code: classCode,
          raw: klass as Record<string, unknown>,
        })
      })
    })
    return options
  }, [divisions])

  useEffect(() => {
    if (classOptions.length === 0) {
      setSelectedClassId('')
      return
    }
    if (!selectedClassId || !classOptions.some(option => option.id === selectedClassId)) {
      setSelectedClassId(classOptions[0].id)
    }
  }, [classOptions, selectedClassId])

  const selectedClass = useMemo(
    () => classOptions.find(option => option.id === selectedClassId) ?? null,
    [classOptions, selectedClassId]
  )

  const defaultCopies = useMemo(() => {
    if (!selectedClass) return 1
    return resolveLabelsPerExhibitFromClass(selectedClass.raw) ?? 1
  }, [selectedClass])

  useEffect(() => {
    setCopiesInput(String(Math.max(1, defaultCopies)))
  }, [defaultCopies, selectedClass?.id])

  const copiesToUse = useMemo(() => {
    const parsed = Number.parseInt(copiesInput, 10)
    if (Number.isNaN(parsed) || parsed < 1) {
      return Math.max(1, defaultCopies)
    }
    return parsed
  }, [copiesInput, defaultCopies])

  const handlePrint = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (!selectedClass) {
        toast.error('Select a class to continue.')
        return
      }
      if (labelPrintingDisabled) {
        toast.error('Label printing is disabled for this show. Create Exhibit ID labels manually.')
        return
      }
      const trimmed = exhibitId.trim().toUpperCase().slice(0, 4)
      if (!trimmed) {
        toast.error('Enter an Exhibit ID to print.')
        return
      }
      if (!isOnline) {
        toast.error('Connect to the network to print labels.')
        return
      }
      if (labelStrategy === 'dymo' && !selectedPrinter) {
        toast.error('Select a DYMO printer before printing.')
        return
      }

      setIsPrinting(true)
      try {
        await printEntryLabel(
          {
            entryCode: trimmed,
            classCode: selectedClass.code ?? undefined,
            showName: show.name,
          },
          {
            copies: copiesToUse,
            jobKey: `label-only:${selectedClass.id}:${trimmed}`,
          }
        )
        toast.success('Label sent to printer')
        setExhibitId('')
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to print label'
        toast.error(message)
      } finally {
        setIsPrinting(false)
      }
    },
    [
      copiesToUse,
      exhibitId,
      labelPrintingDisabled,
      isOnline,
      labelStrategy,
      printEntryLabel,
      selectedClass,
      selectedPrinter,
      show.name,
    ]
  )

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <PageHeading>Label Printing</PageHeading>
        <p className="text-muted-foreground">
          Generate labels by class without enabling entrant or exhibit tracking.
        </p>
      </header>

      {labelPrintingDisabled && <LabelPrintingDisabledNotice />}

      <Card>
        <CardHeader>
          <CardTitle>Print Exhibit Labels</CardTitle>
          <CardDescription>
            Select a class, enter the Exhibit ID, and choose how many copies to print.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingDivisions ? (
            <p className="text-sm text-muted-foreground">Loading classes…</p>
          ) : classOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Add classes to this show before printing labels.
            </p>
          ) : (
            <form onSubmit={handlePrint} className="space-y-4" noValidate>
              {!labelPrintingDisabled && (
                <div className="flex justify-start">
                  <LabelPrinterPopoverControl
                    strategy={labelStrategy}
                    strategyLabel={strategyLabel}
                    printers={printers}
                    selectedPrinter={selectedPrinter}
                    loadingPrinters={loadingPrinters}
                    error={error}
                    onRefresh={refreshPrinters}
                    onSelectPrinter={selectPrinter}
                    onSetStrategy={setStrategy}
                    onTestPrint={testPrint}
                    jobs={jobs}
                  />
                </div>
              )}
              <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(200px,240px)]">
                <div className="space-y-6">
                  <div className="space-y-1.5">
                    <Label htmlFor="label-only-class-select">Class</Label>
                    <select
                      id="label-only-class-select"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={selectedClassId}
                      onChange={event => setSelectedClassId(event.target.value)}
                      disabled={printControlsDisabled}
                    >
                      {classOptions.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="label-only-exhibit-id">Exhibit ID</Label>
                    <Input
                      id="label-only-exhibit-id"
                      value={exhibitId}
                      onChange={event => setExhibitId(event.target.value.toUpperCase().slice(0, 4))}
                      maxLength={4}
                      autoComplete="off"
                      placeholder="e.g., 1A2B"
                      disabled={printControlsDisabled}
                    />
                  </div>
                </div>

                <div className="space-y-1.5 sm:w-40">
                  <Label htmlFor="label-only-copies">Copies</Label>
                  <Input
                    id="label-only-copies"
                    type="number"
                    min={1}
                    value={copiesInput}
                    onChange={event => setCopiesInput(event.target.value)}
                    disabled={printControlsDisabled}
                    inputMode="numeric"
                  />
                  <p className="text-xs text-muted-foreground">
                    Default copies: {Math.max(1, defaultCopies)}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Button type="submit" disabled={printControlsDisabled}>
                  {isPrinting ? 'Printing…' : 'Print Label'}
                </Button>
                {!labelPrintingDisabled && !isOnline && (
                  <p className="text-xs text-destructive">Connect to the network to print.</p>
                )}
                {!labelPrintingDisabled && labelStrategy === 'dymo' && !selectedPrinter && (
                  <p className="text-xs text-muted-foreground">
                    Select a DYMO printer in the toolbar before printing.
                  </p>
                )}
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
